import { cn } from "@gotts.ai/ui";
import { AlertTriangle, Check, ChevronDown, Copy } from "lucide-react";
import { useState } from "react";

import { formatSmartTime } from "../../lib/format";

import type { FunctionResult } from "./types";

interface ResultDisplayProps {
  result: string | null;
  error: { message: string; details: string | null } | null;
  history: FunctionResult[];
}

export function ResultDisplay({ result, error, history }: ResultDisplayProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);

  if (result === null && !error) return null;

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  // Determine display text for result
  const displayResult = (() => {
    if (result === null) return null;
    if (result === "") return null; // empty string
    return result;
  })();

  const isEmptyString = result === "";
  const isEmptyCall =
    result === "(empty)" || result === "(call returned no data)";

  return (
    <div className="mx-3 mb-2 mt-1 space-y-1">
      {/* Error */}
      {error && (
        <div className="border-l-2 border-l-red-500/40 bg-[rgba(239,68,68,0.06)] px-2.5 py-2">
          <div className="flex items-start gap-1.5">
            <AlertTriangle
              size={12}
              className="mt-0.5 shrink-0 text-[var(--color-error)]"
            />
            <span className="text-[11px] text-[var(--color-error)]">
              {error.message}
            </span>
          </div>
          {error.details && (
            <div className="mt-1">
              <button
                type="button"
                onClick={() => {
                  setShowDetails(!showDetails);
                }}
                className="text-[9px] text-[var(--color-text-muted)] underline"
              >
                {showDetails ? "Hide details" : "Show details"}
              </button>
              {showDetails && (
                <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap font-mono text-[9px] text-[var(--color-text-muted)]">
                  {error.details}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {displayResult !== null && (
        <div className="group flex items-start gap-1.5 bg-[rgba(0,0,0,0.4)] px-2.5 py-2">
          <pre className="min-w-0 flex-1 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-[var(--color-text-dim)]">
            {displayResult}
          </pre>
          <button
            type="button"
            onClick={() => handleCopy(displayResult)}
            className="shrink-0 text-[var(--color-text-muted)] opacity-0 transition-opacity group-hover:opacity-100"
            title="Copy"
          >
            {copied ? (
              <Check size={12} className="text-[var(--color-success)]" />
            ) : (
              <Copy size={12} />
            )}
          </button>
        </div>
      )}

      {isEmptyString && (
        <div className="bg-[rgba(0,0,0,0.4)] px-2.5 py-2 font-mono text-[11px] italic text-[var(--color-text-muted)]">
          (empty string)
        </div>
      )}

      {isEmptyCall && (
        <div className="flex items-center gap-1.5 border-l-2 border-l-amber-500/40 bg-[rgba(245,158,11,0.04)] px-2.5 py-2">
          <AlertTriangle
            size={11}
            className="shrink-0 text-[var(--color-warning)]"
          />
          <span className="font-mono text-[11px] text-[var(--color-warning)]">
            Call returned no data
          </span>
        </div>
      )}

      {/* History */}
      {history.length > 1 && (
        <div>
          <button
            type="button"
            onClick={() => {
              setShowHistory(!showHistory);
            }}
            className={cn(
              "flex items-center gap-1 text-[9px] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)]",
            )}
          >
            <ChevronDown
              size={10}
              className={cn(
                "transition-transform",
                showHistory && "rotate-180",
              )}
            />
            Previous results ({history.length - 1})
          </button>
          {showHistory && (
            <div className="mt-1 space-y-1">
              {history.slice(1).map((h, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-[rgba(0,0,0,0.2)] px-2 py-1"
                >
                  <span className="shrink-0 font-mono text-[8px] text-[var(--color-text-muted)]">
                    {formatSmartTime(h.timestamp)}
                  </span>
                  <pre className="min-w-0 flex-1 truncate font-mono text-[9px] text-[var(--color-text-muted)]">
                    {h.value}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
